// CreateSixServiceImpl.ts
import type { SixCreated } from "@/services/abstract/CreateSixService";
import { CreateSixService } from "@/services/abstract/CreateSixService";
import type { FetcherService } from "@/services/abstract/FetcherService";
import type { CreateSixModalInformation, NewExternalUser, SixOwner } from "@/types/six/Six";
import type { DeleteSixResponse } from "@/types/create-six.types";
import { sixEndpoints } from "@/api/endpoints";

/**
 * Implementation of Six creation service handling Six validation, creation,
 * and user management for the Six workspace.
 *
 * Provides functionality for:
 * - Validating Six names
 * - Creating new Sixes with internal and external participants
 * - Fetching organization users
 * - Inviting external users to the organization
 *
 * @see CreateSixService
 */
export class CreateSixServiceImpl extends CreateSixService {
  /**
   * Creates an instance of CreateSixServiceImpl
   * @param fetcher - Service for making HTTP requests to the backend
   */
  constructor(private readonly fetcher: FetcherService) {
    super();
  }

  /**
   * Validates if a Six name is available and meets requirements
   * @endpoint POST /six/validate-name
   * @param name - The Six name to validate
   * @request { name: string }
   * @returns void
   * @throws ValidationError if name is already taken or invalid
   * @tags six, validation
   */
  async validateName(name: string): Promise<void> {
    await this.fetcher.post(sixEndpoints.validateSixName, { name });
  }

  /**
   * Creates a new Six workspace with specified owners and participants
   *
   * Handles both internal and external participants, and can create new
   * external user accounts as part of the Six creation process.
   *
   * Note: Also updates window.sixes for client-side caching.
   *
   * @endpoint POST /six
   * @param payload - Six creation data
   * @param payload.name - Unique name for the Six
   * @param payload.description - Description of the Six purpose
   * @param payload.ownerIds - Array of owner identifiers
   * @param payload.internalParticipantIds - Array of internal user IDs to add as participants
   * @param payload.externalParticipantIds - Array of existing external user IDs to add
   * @param payload.newExternalUsers - Array of new external users to create and add
   * @request { payload: { name: string; description: string; ownerIds: Owner[]; internalParticipantIds: string[]; externalParticipantIds: string[]; newExternalUsers: NewExternalUserDTO[] } }
   * @returns SixCreated object containing the newly created Six ID and name
   * @throws ValidationError if Six data is invalid
   * @throws UnauthorizedError if user lacks permission to create Sixes
   * @throws ConflictError if Six name already exists
   * @tags six, creation, participants
   */
async createSix(payload: {
  name: string;
  description: string;
  ownerIds: SixOwner[];
  internalParticipantIds: string[];
  externalParticipantIds: string[];
  newExternalUsers: NewExternalUser[];
}): Promise<SixCreated> {
  const ownerIds = payload.ownerIds.map(o => o.id);

  const requestBody = {
    name: payload.name,
    description: payload.description,
    ownerIds,
    internalParticipantIds: payload.internalParticipantIds,
    externalParticipantIds: payload.externalParticipantIds,
    newExternalUsers: payload.newExternalUsers,
  };

  const created = await this.fetcher.post<SixCreated>(
    sixEndpoints.createSix,
    requestBody
  );

  return created;
}

/**
   * Updates an existing Six workspace.
   * @endpoint PATCH /private/v1/sixes/:sixId
   */
  async updateSix(id: string, payload: {
    name: string;
    description: string;
    ownerIds: string[];
    internalParticipantIds: string[];
    externalParticipantIds: string[];
    newExternalUsers: NewExternalUser[];
    active?: 'true' | 'false';
  }): Promise<SixCreated> {
    const requestBody = {
      name: payload.name,
      description: payload.description,
      ownerIds: payload.ownerIds,
      internalParticipantIds: payload.internalParticipantIds,
      externalParticipantIds: payload.externalParticipantIds,
      newExternalUsers: payload.newExternalUsers,
      active: payload.active
    };

    const url = sixEndpoints.updateSix(id);

    const updated = await this.fetcher.patch<SixCreated>(
      url,
      requestBody
    );

    return updated;
  }

  async getCreateSixModalInformation(): Promise<CreateSixModalInformation> {
    return await this.fetcher.get(sixEndpoints.createSixModalInfo);
  }

  /**
   * Permanently deletes a SIX.
   * @endpoint DELETE /private/v1/sixes/:id
   * @param id - The ID of the SIX to delete
   * @returns DeleteSixResponse containing commandId for polling
   * @throws UnauthorizedError if user is not the owner
   * @throws NotFoundError if SIX does not exist
   */
  async deleteSix(id: string): Promise<DeleteSixResponse> {
    const url = sixEndpoints.deleteSix(id);
    return await this.fetcher.del<DeleteSixResponse>(url);
  }
}
